import { inferCalibrationRegionFromRegionLabel } from './floodCalibrationRegionInference';
import {
  type FloodCalibrationZoneRegion,
  getDepthScaleForYear,
  getNationalFloodIntensityFactor,
} from './vietnamFloodScenarioCalibration';

/** Vùng ngập sau hiệu chỉnh — dùng chung sidebar, KPI và map 3D. */
export interface FloodRegion {
  name: string;
  depthM: number;
  severity: string;
  status: 'warning' | 'severe';
  areaKm2: number;
  affectedPop: number;
}

/**
 * Miền hiệu chuẩn từ `calibration_region` GeoJSON hoặc suy từ tên vùng.
 */
export function calibrationRegionFromFeatureProperties(
  p: Record<string, unknown>,
): FloodCalibrationZoneRegion {
  const raw = p.calibration_region;
  if (raw === 'north' || raw === 'central' || raw === 'south' || raw === 'highlands') {
    return raw;
  }
  return inferCalibrationRegionFromRegionLabel(String(p.name ?? ''));
}

/**
 * Nhân độ sâu / diện tích / dân số với hệ số cường độ toàn quốc và thang độ sâu theo miền+năm.
 */
export function applyRegionDepthAndIntensity(
  r: FloodRegion,
  year: number,
  calibrationRegion: FloodCalibrationZoneRegion,
): FloodRegion {
  const intensityFactor = getNationalFloodIntensityFactor(year);
  const depthScale = getDepthScaleForYear(year, calibrationRegion);
  return {
    ...r,
    depthM: Math.round(r.depthM * intensityFactor * depthScale * 10) / 10,
    areaKm2: Math.round(r.areaKm2 * intensityFactor * 10) / 10,
    affectedPop: Math.round(r.affectedPop * intensityFactor),
  };
}

/**
 * Đọc thuộc tính GeoJSON vùng ngập (file gộp) và trả về {@link FloodRegion} đã hiệu chỉnh giống sidebar.
 */
export function calibrateFloodRegionFromGeoJsonProperties(
  p: Record<string, unknown>,
  year: number,
): FloodRegion {
  const depthRaw = Number(p.depth_m);
  const areaRaw = Number(p.area_km2 ?? 0);
  const popRaw = Number(p.affected_pop ?? 0);
  const statusRaw = p.status === 'severe' ? 'severe' : 'warning';
  const base: FloodRegion = {
    name: String(p.name ?? ''),
    depthM: Number.isFinite(depthRaw) ? depthRaw : 0,
    severity: String(p.severity ?? ''),
    status: statusRaw,
    areaKm2: Number.isFinite(areaRaw) ? areaRaw : 0,
    affectedPop: Number.isFinite(popRaw) ? Math.floor(popRaw) : 0,
  };
  return applyRegionDepthAndIntensity(base, year, calibrationRegionFromFeatureProperties(p));
}
